import makeStyles from '@mui/styles/makeStyles';
import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  EMIR_EXPORT_ID,
  EMIR_RESPONSE_MODEL_DETAILED,
  downloadEmirExport,
  getEmirExportFileName,
  getEmirResponseModel,
  getEmirReturnedPositionDate,
  useEmirBreakdownQuery,
  type EmirBreakdownPayload,
} from '../../../../api/rs-hg/emir';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { RaptorTheme } from '../../../../styling/theme';
import { useFileExportMutation } from '../../../../hooks/useFileExportMutation';
import RaptorFileDownloadButton from '../../../feedback/RaptorFileDownloadButton.component';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import GridItem from '../../../layout/GridComponents/GridItem';
import DisplayAreaCenteredWrapper from '../../../layout/utilities/displayAreaWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import FundSummaryContainer from './components/FundSummaryContainer/fundSummaryContainer.component';
import StandardMancoSummaryContainer from './components/MancoSummaryContainer/MancoSummaryContainer.component';

const useStyles = makeStyles<RaptorTheme>(() => ({
  root: {
    transition: 'all .3s',
    '&:hover': {
      transform: 'scale(0.99)',
    },
  },
}));

const EMIR: React.FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
}) => {
  const classes = useStyles();
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const emirQuery = useEmirBreakdownQuery(positionDate);
  const { mutate: downloadEmir, status: emirExportStatus } =
    useFileExportMutation({
      mutationKey: [EMIR_EXPORT_ID, positionDate, raptorPlatformId],
      download: downloadEmirExport,
      getFileName: getEmirExportFileName,
      errorMessage: 'EMIR export failed. Please try again.',
    });
  const isEmirExportDisabled = emirExportStatus !== 'idle';

  const [selectedKey, setSelectedKey] = useState('');
  const updateSelectedKey = (key: string) => {
    setSelectedKey(key);
  };

  useEffect(() => {
    setSelectedKey('');
  }, [positionDate]);

  return (
    <QueryBoundary<EmirBreakdownPayload>
      isLoading={emirQuery.isLoading}
      isFetching={emirQuery.isFetching}
      isError={emirQuery.isError}
      error={emirQuery.error}
      data={emirQuery.data?.data}
      loadingMessages={[
        'Loading Manco Level Data',
        'Manco Level Data May Take a Few Moments to Calculate',
        '...',
      ]}
      errorMessage="There was an error retrieving EMIR data."
      noDataMessage="No EMIR data available."
    >
      {(payload) => {
        const isDetailed =
          getEmirResponseModel(payload) === EMIR_RESPONSE_MODEL_DETAILED;
        const returnedPositionDate = getEmirReturnedPositionDate(
          emirQuery.data,
        );
        const fundsCount = emirQuery.data?.funds.length ?? payload.funds.length;

        return (
          <Grid container spacing={2} style={{ padding: 8 }}>
            {isDetailed && positionDate ? (
              <GridItem
                card
                key="export"
                item
                xs={12}
                cardStyle={{ padding: '0.62rem' }}
              >
                <div className="flex w-[31.25rem] flex-col gap-2.5 rounded border-2 border-[#f1f1f1] bg-white p-2.5 shadow-[0px_2px_5px_#f1f1f1]">
                  <div className="text-xl font-semibold text-mainblue">
                    EMIR Export (.xlsx)
                  </div>
                  <div
                    className={`my-[0.31rem] w-[12.5rem] ${
                      isEmirExportDisabled ? 'pointer-events-none' : ''
                    }`}
                    onClick={() => {
                      if (isEmirExportDisabled) {
                        return;
                      }
                      downloadEmir({ positionDate, raptorPlatformId });
                    }}
                  >
                    <RaptorFileDownloadButton status={emirExportStatus} />
                  </div>
                </div>
              </GridItem>
            ) : null}
            <GridItem
              card
              key="manco-overview"
              item
              classes={classes}
              cardStyle={{ height: '18.75rem' }}
              xs={12}
            >
              <StandardMancoSummaryContainer
                payload={payload}
                fundsCount={fundsCount}
                returnedPositionDate={returnedPositionDate}
                selectedKey={selectedKey}
                setSelectedKeyFunction={updateSelectedKey}
              />
            </GridItem>
            <GridItem
              card
              key="fund-overview"
              item
              cardStyle={{ minHeight: '18.75rem' }}
              xs={12}
            >
              {selectedKey !== '' ? (
                <FundSummaryContainer
                  payload={payload}
                  selectedKey={selectedKey}
                  variant={isDetailed ? 'detailed' : 'standard'}
                  exportPositionDate={positionDate}
                />
              ) : (
                <DisplayAreaCenteredWrapper noMinHeight>
                  <Typography variant="h1">
                    {'Please Click on a Chart Above to Show Fund Data.'}
                  </Typography>
                </DisplayAreaCenteredWrapper>
              )}
            </GridItem>
          </Grid>
        );
      }}
    </QueryBoundary>
  );
};

export default EMIR;
